/**
 * How a Patch's video is laid out (components/PatchDock.tsx): the film strip it leaves on screen
 * when someone's video is on and the panel isn't open (and the same strip in its picture-in-picture
 * window), which corner that strip sits in, and how the tiles fill a full-screen Patch. Plain
 * functions, without LiveKit or the page, so they're tested on their own (test/patch.test.ts).
 */

/** Where the film strip sits: top or bottom, left or right. */
export type Corner = 'tl' | 'tr' | 'bl' | 'br';

const CORNERS: readonly Corner[] = ['tl', 'tr', 'bl', 'br'];
export const isCorner = (x: unknown): x is Corner => typeof x === 'string' && (CORNERS as readonly string[]).includes(x);

/** Bottom right until the person moves it: clear of the menu, the page's title and the box to write in. */
export const DEFAULT_CORNER: Corner = 'br';

export interface StripPerson {
  id: string;
  name: string;
  me: boolean;
}

export interface StripTile {
  /** For React: someone's shared screen and their camera can both be on the strip. */
  key: string;
  kind: 'screen' | 'person';
  id: string;
  name: string;
  me: boolean;
  /** A person's camera is on (otherwise their photo or initials show). A screen is always video. */
  video: boolean;
}

/**
 * The tiles on the film strip, at most `max`, and how many more are in the Patch ("+2"): the
 * screen being watched first (then any other shared screens), then whoever spoke last, then the
 * others with their camera on, then the rest, in the order they came in; this person's own camera
 * last, and only while it's on. Whoever spoke last stays first until someone else speaks, so the
 * tiles don't jump about each time someone says a word.
 */
export function stripTiles(input: {
  people: readonly StripPerson[];
  /** Whose camera is on. */
  videos: readonly { id: string }[];
  /** Who is sharing a screen (others: this person's own isn't shown back to them). */
  screens: readonly { id: string; name: string }[];
  watching: string | null;
  /** The other person who spoke most recently. */
  lastSpeaker: string | null;
  max: number;
}): { tiles: StripTile[]; more: number } {
  const camera = new Set(input.videos.map((v) => v.id));
  const screens = input.screens.map((s, i) => ({ s, i })).sort((a, b) => Number(b.s.id === input.watching) - Number(a.s.id === input.watching) || a.i - b.i);
  const rank = (p: StripPerson) => (p.id === input.lastSpeaker ? 0 : camera.has(p.id) ? 1 : 2);
  const others = input.people
    .filter((p) => !p.me)
    .map((p, i) => ({ p, i }))
    .sort((a, b) => rank(a.p) - rank(b.p) || a.i - b.i)
    .map((x) => x.p);
  const me = input.people.find((p) => p.me && camera.has(p.id));
  const all: StripTile[] = [
    ...screens.map(({ s }) => ({ key: `screen:${s.id}`, kind: 'screen' as const, id: s.id, name: s.name, me: false, video: true })),
    ...others.map((p) => ({ key: `person:${p.id}`, kind: 'person' as const, id: p.id, name: p.name, me: false, video: camera.has(p.id) })),
    ...(me ? [{ key: `person:${me.id}`, kind: 'person' as const, id: me.id, name: me.name, me: true, video: true }] : [])
  ];
  const max = Math.max(1, Math.floor(input.max) || 1);
  return { tiles: all.slice(0, max), more: Math.max(0, all.length - max) };
}

/** The corner nearest where the strip was let go (its middle, in the window). */
export function nearestCorner(x: number, y: number, width: number, height: number): Corner {
  return `${y < height / 2 ? 't' : 'b'}${x < width / 2 ? 'l' : 'r'}` as Corner;
}

/** Moving the strip with the arrow keys: up, down, left or right to the next corner. */
export function cornerToward(corner: Corner, key: string): Corner {
  const [v, h] = [corner[0], corner[1]];
  switch (key) {
    case 'ArrowUp':
      return `t${h}` as Corner;
    case 'ArrowDown':
      return `b${h}` as Corner;
    case 'ArrowLeft':
      return `${v}l` as Corner;
    case 'ArrowRight':
      return `${v}r` as Corner;
    default:
      return corner;
  }
}

/** How many tiles of `tile` pixels (plus the gap between) fit in `space`: at least one, at most `max`. */
export function tilesThatFit(space: number, tile: number, gap: number, max: number): number {
  if (!(tile > 0)) return 1;
  return Math.min(Math.max(1, Math.floor((space + gap) / (tile + gap))), Math.max(1, max));
}

/**
 * A full-screen Patch: the columns, rows and tile size that show `n` cameras as large as they can
 * be in `width`×`height` without cutting any off (each tile keeps the camera's shape, 16:9).
 */
export function galleryLayout(n: number, width: number, height: number, gap = 8, aspect = 16 / 9): { cols: number; rows: number; width: number; height: number } {
  const count = Math.max(1, Math.floor(n) || 1);
  let best = { cols: 1, rows: count, width: 0, height: 0 };
  for (let cols = 1; cols <= count; cols++) {
    const rows = Math.ceil(count / cols);
    const w = (width - gap * (cols - 1)) / cols;
    const h = (height - gap * (rows - 1)) / rows;
    if (w <= 0 || h <= 0) continue;
    const tileW = Math.floor(Math.min(w, h * aspect));
    if (tileW > best.width) best = { cols, rows, width: tileW, height: Math.floor(tileW / aspect) };
  }
  return best;
}

const KEY = 'stitch:patch-strip-corner';

/** The corner this person last put the strip in, on this device. */
export function savedCorner(): Corner {
  try {
    const v = globalThis.localStorage?.getItem(KEY);
    return isCorner(v) ? v : DEFAULT_CORNER;
  } catch {
    return DEFAULT_CORNER;
  }
}

export function saveCorner(corner: Corner): void {
  try {
    globalThis.localStorage?.setItem(KEY, corner);
  } catch {
    // Storage blocked (a private window): it stays there until the page is reloaded.
  }
}
